// Copyright (c) 2019-24 Stax·ai, Inc.
// See LICENSE.md file in project root directory

import React from 'react';
import { BrowserRouter as Router, Switch, Route } from 'react-router-dom';
import HttpsRedirect from 'react-https-redirect';
import { ToastContainer, toast } from 'react-toastify';

// Global styles
import './styles/style.css';
import './styles/forms.css';
import './styles/toast.css';
import './styles/layout.css';
import { THEME_LIGHT, THEME_DARK } from './styles/themes.js';

// App-level Components
import Loader from './components/Loader';
import SLA from './components/SLA';
import Login from './components/Login';
import GetStarted from './components/GetStarted';
import MyStacks from './components/MyStacks';
import Account from './components/Account';
import Settings from './components/Settings';
import Welcome from './components/Welcome';
import Invite from './components/Invite';
import Public from './components/Public/index.js';
import Timeline from './components/Timeline/index.js';
import Slack from './components/Misc/Slack';
import VerifyLogin from './components/Modals/VerifyLogin';
import SetPassword from './components/SetPassword';
import Intercom from './intercom';
import Investments from './components/Investments';

import useDarkMode from './utilities/darkMode';

import { datadogRum } from '@datadog/browser-rum';

// API
import api from './api';

class App extends React.Component {

	state = {

		// Team state
		team: {
			_id: null,
			users: []
		},

		// User state
		user: {
			_id: null, 
			enableDarkMode: localStorage.getItem('theme') === 'dark'
		},

		// Root loader
		loading: 0,
		loadingKeys: [],

		// Login
		persist: false,

		// 2FA
		modal: "",
		method: "",
	}

	// Keep track of cleared notifications to prevent a race condition
	clearedNotifications = []
	loadingTimeout = null
	loadingComplete = false

	componentDidMount = async () => {
		// Set the switchTeam function on the API
		api.setTeamSwitcher(this.switchTeam);

		await this.whoAmI();
		let lastLocalSync = this.state.user?.lastLocalSync ?? null 
		if(lastLocalSync){
			const currentTime = new Date()
			// if last access is found and if one day has passed, show the sync toast
			lastLocalSync = new Date(lastLocalSync)
			if (currentTime - lastLocalSync > 3600000 && !localStorage.getItem('syncError')) {
				localStorage.setItem('syncError', true)
				toast.info("Your Stax·ai Sync app hasn't checked in for a while. Please make sure that you are still logged in to it so documents get saved.")
			}
		}

		// Notifications interval
		this.notificationsInterval = setInterval(async () => {

			if (!this.state.user || !this.state.user._id)
				return;

			const res = await api.get('/user/fetchNotifications', {}, true);
			
			if (res && res.notifications) {
				// Remove recently cleared notifications
				let notifications = res.notifications.slice();
				let cleared = notifications.filter(n => this.clearedNotifications.includes(n._id));

				while (cleared.length > 0) {
					const notificationIdx = notifications.findIndex(n => n._id === cleared[0]);
					notifications.splice(notificationIdx,1);
					cleared.splice(0,1);
				}

				const notified = notifications.length > this.state.user.notifications.length;
				await this.setState({ user: {
					...this.state.user,
					notifications,
					notified: notified ? true : this.state.user.notified
				} });
			}
			
		}, 30000);

	}

	componentWillUnmount = () => {
		if (this.notificationsInterval)
			clearInterval(this.notificationsInterval);
	}

	whoAmI = async () => {
		// Get user info
		const res = await api.get('/user/whoAmI', {}, true);
		if (!res?.user || !res?.team) return;

		const enableDarkMode = res.user.enableDarkMode ? true : window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
		localStorage.setItem('theme', enableDarkMode ? 'dark' : 'light');


		const lastSync = await api.get('/team/lastLocalSync', {}, true);
		const user = {
			...res.user,
			enableDarkMode: enableDarkMode,
			lastLocalSync: res.user?.lastLocalSync ||  (lastSync?.lastLocalSync ?? null)
		};
		await this.setState({
			user: user, 
			team: res.team
		});


		datadogRum.setUser({
			id: res.user._id, 
			email: res.user.email, 
			name: res.user.name, 
		});

		datadogRum.setAccount({
			id: res.team._id,
			name: res.team.name,
		});

	}
	
	resetUser = () => this.setState({
		user: {
			_id: null,
			// enableDarkMode: window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
		}
	});


	loading = async (z=999, message, long=false, key) => {
		if (this.loadingTimer)
			clearInterval(this.loadingTimer);
		
		// If we have a key, add it.
		if(key && !this.state.loadingKeys.includes(key))
			this.setState(prevState => {
				return {loadingKeys: [...prevState.loadingKeys, key]}
			});
		
		// Custom override message
		if (message)
			return this.setState({ loading: message, loadingZ: z, loadingLong: long });

		// Standard loading system...
		this.loadingTimer = setInterval(() => this.setState({ loading: this.state.loading + 1 }), 1000);

		
		this.setState({ loading: 1, loadingZ: z, loadingLong: long});
		
		// this.loadingTimeout = setTimeout(() => {
		// 	if (this.loadingComplete)
		// 		this.setState({ loading: 0 });
		// 	this.loadingTimeout = null;
		// 	this.loadingComplete = false
		// }, 700);
	}

	loaded = (key) => {
		let keys = [...this.state.loadingKeys??[]] 
		if(key && !keys.includes(key)) // If no key, or key does not exist in this.state.loadingKeys... clear it. 
			keys = [];
		else if(key)// Find the key from the list and remove it. 
			keys.splice(keys.indexOf(key), 1)

		// Update the keys, in state 
		this.setState({loadingKeys: keys})

		// If the length of loading keys is > 0, we are still waiting for something to load. Do not close the loading modal. 
		if(keys.length > 0)
			return
		
		// We are waiting for nothing to load, clear the screen. 
		if (this.loadingTimer)
			clearInterval(this.loadingTimer);
		
		// this.loadingComplete = true;
		if (this.loadingTimeout)
			return;

		setTimeout(() => this.setState({ loading: 0 }), 100);
	}

	notify = async (userId, notification) => {
		await api.post('/user/notify', {
			userId,
			notification
		});
	}

	// User settings update
	updateUser = async (user, cloud=true) => {

		let users = this.state.team.users.slice();
		const userIdx = users.findIndex(u => u._id === user._id);
		users[userIdx] = {
			...users[userIdx],
			...user
		};

		let stateUpdate = {
			team: {
				...this.state.team,
				users
			}
		};

		if (this.state.user._id === user._id)
			stateUpdate.user = {
				...this.state.user,
				...user
			};

		await this.setState(stateUpdate);

		if (!cloud)
			return;

		// API update
		await api.post('/user/update', {
			userId: user._id,
			userDiff: {
				...user,
				_id: undefined
			}
		});

	}

	// Clear user notification
	clearNotification = async (idx) => {
        let notifications = this.state.user.notifications.slice();
        this.clearedNotifications.push(notifications[idx]._id);
        notifications.splice(idx, 1);
        this.updateUser({ _id: this.state.user._id, notifications });
    }

	// Team settings update
	updateTeam = async (team, cloud=true) => {
		await this.setState({ team: {
			...this.state.team,
			...team
		} });

		if (!cloud)
			return;

		// API update
		await api.post('/team/update', { team });
	}

	updatePersist = async persist => await this.setState({persist})

	// Switch team membership
	switchTeam = async (team, redirect) => {
		this.setState({newTeam: team})
		if (team === this.state.team?._id)
			return toast.info("You're already logged in to this team!");

		const user = this.state.user;
		this.loading(10000, 'Switching team...');

		setTimeout(async () => {
			const resAuth = await api.post('/user/switchTeamAuth', { user, team });
			
			// 2FA Required
			if (resAuth?.twoFactorAuth) {
				if (resAuth.success) {
					this.loaded();
					this.setState({
						modal: "verify-code",
						method: resAuth.twoFactorAuth
					})
				} else {
					toast.error(resAuth.error);
				}
			} else if (resAuth.success) {
				// This request updates the cookies
				const res = await api.post('/user/switchTeam', { team });
				if (!res?.success) return this.loaded();

				// Force reload the page/redirect
				window.location.href = redirect ?? "/";

				// Update datadog user info 
				datadogRum.setAccount({
					id: team, 
					name: res.team.name,	 
				})
			} else {
				// Unsuccessful - API throws error
				this.loaded();
			}
		}, 200);
	}

	// Invite new user
	inviteUser = async (userEmail) => {
		
		await this.loading();

		const res = await api.post('/user/invite', { userEmail });

		if (res && res.user) {

			await this.setState({
				team: {
					...this.state.team,
					users: [
						...this.state.team.users,
						res.user
					]
				}
			});
			toast.info("Successfully sent invite!");

		}
		// else toast.error("Unable to invite user!"); 
		
		this.loaded();

	}

	// Delete user account
	deleteUser = async (userId) => {

		let users = this.state.team.users.slice();
		const userIdx = users.findIndex(u => u._id === userId);
		users.splice(userIdx, 1);

		await this.setState({
			team: {
				...this.state.team,
				users
			}
		});

		await api.post('/user/remove', { userId });

	}

	render = () => {
		const dark = useDarkMode(this.state.user);
		return <HttpsRedirect>
			<div
				className="body"
				style={dark ? THEME_DARK : THEME_LIGHT}
			>
				<Intercom user={this.state.user} team={this.state.team} />
				<Router>

					{/* Toast container */}
					<ToastContainer
						position="top-center"
						autoClose={5000}
					/>

					{/* Loader */}
					{(typeof this.state.loading === "string" || typeof this.state.loading === 'function'|| this.state.loading > 0) && <Loader
						loading={this.state.loading}
						long={this.state.loadingLong}
						zIndex={this.state.loadingZ}
						user={this.state.user}
					/>}

					{/* Welcome message */}
					{this.state.user && this.state.user.welcomed === false && !window.location.href.includes("invite") && <Welcome
						user={this.state.user}
						team={this.state.team}
						pulseUserIcon={() => this.setState({ user: {
							...this.state.user,
							notified: true
						} })}
						finish={() => this.updateUser({
							_id: this.state.user._id,
							welcomed: true
						})}
					/>}

					{/* Top-level routing */}
					<Switch>
						
						{/* Login page */}
						<Route
							path="/login"
							render={props => <Login
								{...props}
								loading={this.loading}
								loaded={this.loaded}
								login={async (user,team) => await this.setState({ user, team })}
								reset={this.resetUser}
								persist={this.state.persist}
								updatePersist={this.updatePersist}
								/>}
						/>

						<Route 
							path='/mfa/:userId/:team/:method'
							render={props => <VerifyLogin
								{...props}
								persist={this.state.persist}
								login={async (user,team) => await this.setState({ user, team })}
								switchingTeam={false}
								resendCode={this.switchTeam}
								loading={this.loading}
								loaded={this.loaded}
								noClose={true}
							/>}
						/>

						{/* Get started page (new user onboarding) */}
						<Route
							path="/start"
							render={props => <GetStarted
								{...props}
								whoAmI={this.whoAmI}
							/>}
						/>

						{/* SLA/Master SaaS Agreement */}
						<Route
							path="/sla/:plan"
							render={props => <SLA
								{...props}
								loading={this.loading}
								loaded={this.loaded}
							/>}
						/>

						{/* Reset password page */}
						<Route
							path="/reset/:userId/:resetToken"
							render={props => <SetPassword
								{...props}
							/>}
						/>

						{/* User invitation page */}
						<Route
							path="/invite/:userId"
							render={props => <Invite
								{...props}
								whoAmI={this.whoAmI}
							/>}
						/>

						{/* Account settings page */}
						<Route
							path="/settings/account"
							render={props => <Account
								{...props}
								user={this.state.user}
								team={this.state.team}
								updateUser={this.updateUser}
								switchTeam={this.switchTeam}
								clearNotification={this.clearNotification}
								loading={this.loading}
								loaded={this.loaded}
							/>}
						/>

						{/* Team settings page */}
						<Route
							path="/settings"
							render={props => <Settings
								{...props}
								user={this.state.user}
								updateUser={this.updateUser}
								switchTeam={this.switchTeam}
								clearNotification={this.clearNotification}
								team={this.state.team}
								updateTeam={this.updateTeam}
								inviteUser={this.inviteUser}
								deleteUser={this.deleteUser}
								loading={this.loading}
								loaded={this.loaded}
								notify={this.notify}
							/>}
						/>

						{/* Investments page */}
						<Route
							path="/investments"
							render={props => <Investments
								{...props}
								user={this.state.user}
								updateUser={this.updateUser}
								switchTeam={this.switchTeam}
								clearNotification={this.clearNotification}
								team={this.state.team}
								updateTeam={this.updateTeam}
								loading={this.loading}
								loaded={this.loaded}
								notify={this.notify}
							/>}
						/>

						{/* Public document access */}
						<Route
							path="/public/:documentId"
							render={props => <Public
								{...props}
								user={this.state.user}
								updateUser={this.updateUser}
								switchTeam={this.switchTeam}
								clearNotification={this.clearNotification}
								team={this.state.team}
								loading={this.loading}
								loaded={this.loaded}
							/>}
						/>

						{/* Other/misc stuff */}
						<Route
							path="/misc/slack"
							render={props => <Slack
								{...props}
								user={this.state.user}
								team={this.state.team}
								loading={this.loading}
							/>}
						/>

						{/* Timeline view */}
						<Route
							path="/timeline/:stackId"
							render={props => <Timeline
								{...props}
								user={this.state.user}
								team={this.state.team}
								updateUser={this.updateUser}
								switchTeam={this.switchTeam}
								notify={this.notify}
								clearNotification={this.clearNotification}
								loading={this.loading}
								loaded={this.loaded}
							/>}
						/>

						<Route
							// All other routes go to stacks/document management
							// default
							render={props => <MyStacks
								{...props}
								user={this.state.user}
								updateUser={this.updateUser}
								switchTeam={this.switchTeam}
								clearNotification={this.clearNotification}
								team={this.state.team}
								loading={this.loading}
								loaded={this.loaded}
								notify={this.notify}
							/>}
						/>
						
					</Switch>

				</Router>
				{this.state.modal === "verify-code" ? <VerifyLogin
					modal={() => this.setState({ modal: "" })}
					userId={this.state.user._id}
					team={ this.state.newTeam }
					persist={this.state.persist}
					login={async (user,team) => await this.setState({ user, team })}
					method={this.state.method}
					switchingTeam={true}
					resendCode={this.switchTeam}
					loading={this.loading}
					loaded={this.loaded}
				/> : ""}
			</div>
		</HttpsRedirect>;
	}

}

export default App;
